import { getResource } from "../../util/getResourcePermission";
import { getRoles } from "../../util/getRoles";
import config from "../../permission/config.json";

// Client-side only index for the "HRMS Modules" pseudo-category in Global
// Search - no API call, same pattern as actionsSearchIndex.js/settingsSearchIndex.js.
// No AI/LLM involved, this is a plain static keyword -> route lookup.
//
// Every route/resource/role below was confirmed this session against the real
// newRoutes.js and permission/config.json (a route audit, not guesswork).
// Entries intentionally left OUT, found during that same audit - don't add
// them back without re-verifying first:
// - `employee-tickets` (resource EMPLOYEE_TICKET): despite the self-service-
//   sounding URL, it actually renders the admin/TL ticket list, not "my tickets".
// - `compoff-leave-application` bare listing route: has no permission key at
//   all in newRoutes.js.
// - `onboarding-master` / `onboarding-initiation-master` / `candidate-approval-master`:
//   all have `permission: []` in newRoutes.js with unknown internal gating.
// - resources FLEXI_LEAVE_APPROVAL and LEAVE_CANCELLATION_APPROVAL: referenced
//   in newRoutes.js but don't exist in permission/config.json RESOURCES, so
//   getResource against them always fails closed - harmless but pointless,
//   don't copy that pattern here.
//
// "Payroll" below has `resource: null` on purpose, same as "Book a Meeting
// Room"/"Change Password" in actionsSearchIndex.js - its real route has no
// resource gate in newRoutes.js at all, it's role-gated internally via
// getRoles(["Admin"]) inside the component itself, so the `roles` array below
// is the ONLY gate for that one entry.
export const MODULE_SEARCH_INDEX = [
  // Self-service - no extra roles needed, resource gate alone is sufficient.
  {
    id: "regularize-attendance",
    label: "Regularize Attendance",
    keywords: ["regularization", "regularize", "miss punch", "missed punch"],
    route: "/employee-attendence-regularization",
    resource: config.RESOURCES.ATTENDANCE_REGULARIZATION,
  },
  {
    id: "apply-wfh",
    label: "Apply for WFH",
    keywords: ["wfh", "work from home", "apply wfh"],
    route: "/add-work-from-home-application",
    resource: config.RESOURCES.WHF_APPLICATIONS,
  },
  {
    id: "my-wfh-requests",
    label: "My WFH Requests",
    keywords: ["my wfh", "wfh requests", "wfh status"],
    route: "/work-from-home-application",
    resource: config.RESOURCES.LEAVE,
  },
  {
    id: "apply-compoff",
    label: "Apply for Comp-off",
    keywords: ["comp-off", "compoff", "comp off"],
    route: "/compoff-leave-application/add-compoff-leave",
    resource: config.RESOURCES.C_OFF_APPLICATIONS,
  },
  {
    id: "submit-expense",
    label: "Submit Expense",
    keywords: ["expense", "submit expense", "new expense"],
    route: "/expense/submit",
    resource: config.RESOURCES.EXPENSE_SUBMISSION,
  },
  {
    id: "request-advance",
    label: "Request Advance",
    keywords: ["advance", "expense advance", "request advance"],
    route: "/expense/advances",
    resource: config.RESOURCES.EXPENSE_ADVANCE,
  },
  {
    id: "bill-reimbursement",
    label: "Bill Reimbursement",
    keywords: ["reimbursement", "bill reimbursement", "claim"],
    route: "/bill-reimbursement",
    resource: config.RESOURCES.BILLREIMBURSEMENT,
  },
  {
    id: "my-tickets",
    label: "My Tickets",
    keywords: ["ticket", "helpdesk", "support ticket", "raise ticket"],
    route: "/tickets",
    resource: config.RESOURCES.TICKET,
  },
  {
    id: "file-resignation",
    label: "File Resignation",
    keywords: ["resignation", "resign", "quit"],
    route: "/resignation",
    resource: config.RESOURCES.SEPARATION,
  },
  {
    id: "my-resignation-status",
    label: "My Resignation Status",
    keywords: ["my resignation", "resignation status"],
    route: "/resignation-userlist",
    resource: config.RESOURCES.SEPARATION,
  },
  {
    id: "my-approvals",
    label: "My Approvals",
    keywords: ["approval", "approvals", "pending approvals", "my approvals"],
    route: "/my-approvals",
    resource: config.RESOURCES.APPROVAL_INBOX,
  },

  // Admin/manager-scope - resource gate + explicit roles array (defense in
  // depth), since these are company-or-team-wide pages, not self-service.
  {
    id: "attendance-regularization-admin",
    label: "Attendance Regularization (Admin)",
    keywords: ["attendance regularization", "regularization approval", "regularization requests"],
    // Exact misspelling is the real route path in newRoutes.js, kept verbatim.
    route: "/admin-regularizatrion",
    resource: config.RESOURCES.ADMIN_REGULARIZATION,
    roles: ["Admin", "HR"],
  },
  {
    id: "wfh-approvals",
    label: "WFH Approvals",
    keywords: ["wfh approval", "wfh approvals", "pending wfh"],
    route: "/admin-work-from-home-approval",
    resource: config.RESOURCES.WHF_MANAGEMENT,
    roles: ["Admin", "HR"],
  },
  {
    id: "compoff-approvals",
    label: "Comp-off Approvals",
    keywords: ["comp-off approval", "compoff approval"],
    route: "/admin-compoff-approval",
    resource: config.RESOURCES.C_OFF_MANAGEMENT,
    roles: ["Admin", "HR"],
  },
  {
    id: "expense-approvals",
    label: "Expense Approvals",
    keywords: ["expense approval", "pending expense"],
    route: "/expense/approvals",
    resource: config.RESOURCES.EXPENSE_APPROVAL,
    roles: ["Admin", "HR", "Reporting Manager", "Assistance Reporting Manager"],
  },
  {
    id: "expense-dashboard",
    label: "Expense Dashboard",
    keywords: ["expense dashboard"],
    route: "/expense/dashboard",
    resource: config.RESOURCES.EXPENSE_DASHBOARD,
    roles: ["Admin", "HR"],
  },
  {
    id: "expense-reports",
    label: "Expense Reports",
    keywords: ["expense reports", "expense report"],
    route: "/expense/reports",
    resource: config.RESOURCES.EXPENSE_REPORTS,
    roles: ["Admin", "HR"],
  },
  {
    id: "reimbursement-approvals",
    label: "Reimbursement Approvals",
    keywords: ["reimbursement approval", "bill reimbursement approval"],
    route: "/admin-bill-reimbursement/submit",
    resource: config.RESOURCES.BILLREIMBURSEMENT_APPROVAL,
    roles: ["Admin", "HR"],
  },
  {
    id: "manage-tickets",
    label: "Manage Tickets",
    keywords: ["manage tickets", "ticket management"],
    route: "/manage-tickets",
    resource: config.RESOURCES.MANAGE_TICKET,
    roles: ["Admin", "HR"],
  },
  {
    id: "onboarding",
    label: "Onboarding",
    keywords: ["onboarding", "new joiner onboarding"],
    route: "/onboarding-initiation",
    resource: config.RESOURCES.ON_BOARDING_INITIATION,
    roles: ["Admin", "HR"],
  },
  {
    id: "resignation-management",
    label: "Resignation Management",
    keywords: ["resignation list", "resignation management", "separation"],
    route: "/resignation-employee-list",
    resource: config.RESOURCES.SEPARATION_MANAGEMENT,
    roles: ["Admin", "HR"],
  },
  {
    id: "department-management",
    label: "Department Management",
    keywords: ["department", "departments"],
    route: "/admin-department",
    resource: config.RESOURCES.DEPARTMENTS,
    roles: ["Admin", "HR"],
  },
  {
    id: "branch-management",
    label: "Branch Management",
    keywords: ["branch", "branches"],
    route: "/admin-branch",
    resource: config.RESOURCES.BRANCH_OPTIONS,
    roles: ["Admin", "HR"],
  },
  {
    id: "designation-management",
    label: "Designation Management",
    keywords: ["designation", "designations"],
    route: "/admin-designation",
    resource: config.RESOURCES.DESIGNATION,
    roles: ["Admin", "HR"],
  },
  {
    id: "employee-management",
    label: "Employee Management",
    keywords: ["employee management", "add employee", "manage employees"],
    route: "/employee-master",
    resource: config.RESOURCES.EMPLOYEE_MASTER,
    roles: ["Admin", "HR"],
  },
  {
    id: "payroll",
    label: "Payroll",
    keywords: ["payroll"],
    route: "/admin/zoho-payroll-integration",
    resource: null,
    roles: ["Admin"],
  },
];

export const searchModuleIndex = (query) => {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return MODULE_SEARCH_INDEX.filter(
    (entry) =>
      entry.keywords.some((keyword) => keyword.includes(q) || q.includes(keyword)) &&
      (!entry.resource || getResource(entry.resource, ["read"])) &&
      (!entry.roles || getRoles(entry.roles))
  );
};
